import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Elements a page break must never cut through.
const KEEP_TOGETHER = 'tr, p, li, h1, h2, h3, h4, h5, h6, img, svg, canvas, [data-pdf-keep]';
// Elements that should not be left alone at the bottom of a page.
const KEEP_WITH_NEXT = 'h1, h2, h3, h4, h5, h6, thead, [data-pdf-keep-next]';
// Minimum content (CSS px) that must follow a keep-with-next element on the same page.
const KEEP_WITH_NEXT_MIN = 60;

// Vertical extents of the matching elements, in CSS px relative to `root`.
const extentsOf = (root, selector) => {
  const top0 = root.getBoundingClientRect().top;
  return Array.from(root.querySelectorAll(selector))
    .map((n) => n.getBoundingClientRect())
    .filter((r) => r.height > 0)
    .map((r) => ({ top: r.top - top0, bottom: r.bottom - top0 }));
};

// Like extentsOf for every element, plus where its content starts (inside border + padding).
const blockExtentsOf = (root) => {
  const top0 = root.getBoundingClientRect().top;
  return Array.from(root.querySelectorAll('*'))
    .map((n) => ({ n, r: n.getBoundingClientRect() }))
    .filter(({ r }) => r.height > 0)
    .map(({ n, r }) => {
      const cs = getComputedStyle(n);
      const top = r.top - top0;
      return {
        top,
        bottom: r.bottom - top0,
        contentTop: top + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0),
      };
    });
};

// Share of a page we're willing to leave blank to keep a block (card, section) whole.
const MAX_WASTE = 0.5;

// Move a proposed break up until it no longer splits an element.
// - `keep` elements are never split (unless taller than a page).
// - Any other block is kept whole if it fits on a page and moving it leaves
//   at most MAX_WASTE of the page blank; otherwise the break falls inside it.
// - A break that would leave only a block's top border/padding on this page
//   moves above the block, whatever the waste (no empty card slivers).
// Falls back to the hard break when nothing better exists.
const safeBreak = (y, start, pageCss, keep, blocks, keepNext) => {
  let cut = y;
  let moved = true;
  const ok = (e) => e.top > start + 1 && e.top < cut && e.bottom > cut;
  while (moved) {
    moved = false;
    for (const e of keep) {
      if (ok(e)) { cut = e.top; moved = true; }
    }
    for (const e of blocks) {
      const fits = e.bottom - e.top <= pageCss;
      const sliver = cut <= e.contentTop + 2;
      if (ok(e) && (sliver || (fits && y - e.top <= pageCss * MAX_WASTE))) { cut = e.top; moved = true; }
    }
    for (const e of keepNext) {
      if (e.top > start + 1 && e.top < cut && e.bottom + KEEP_WITH_NEXT_MIN > cut) { cut = e.top; moved = true; }
    }
  }
  return cut > start + 1 ? cut : y;
};

// The colour behind the node (first non-transparent background up the tree), as [r, g, b].
const backgroundOf = (el) => {
  for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
    const m = getComputedStyle(n).backgroundColor.match(/[\d.]+/g);
    if (m && (m.length < 4 || Number(m[3]) > 0)) return m.slice(0, 3).map(Number);
  }
  return [255, 255, 255];
};

// PDFs are always light: read the background as the light theme paints it, then restore
// the viewer's theme in the same frame (no visible flash).
const lightBackgroundOf = (el) => {
  const root = document.documentElement;
  const prev = root.getAttribute('data-theme');
  root.setAttribute('data-theme', 'light');
  try {
    return backgroundOf(el);
  } finally {
    if (prev) root.setAttribute('data-theme', prev);
    else root.removeAttribute('data-theme');
  }
};

// True when the canvas can't be read back (cross-origin or tainting content was drawn).
const isTainted = (canvas) => {
  try {
    canvas.getContext('2d').getImageData(0, 0, 1, 1);
    return false;
  } catch (e) {
    return true;
  }
};

const capture = (el, width, height, skipSvg, bg) => html2canvas(el, {
  scale: 1.5,
  backgroundColor: `rgb(${bg.join(',')})`,
  useCORS: true,
  allowTaint: false,
  width,
  height,
  windowWidth: Math.max(width, document.documentElement.clientWidth),
  windowHeight: height,
  scrollX: 0,
  scrollY: 0,
  // Hide decoration (html.pdf-capturing ...) and always capture in the light theme,
  // whatever the viewer uses.
  onclone: (doc) => {
    doc.documentElement.classList.add('pdf-capturing');
    doc.documentElement.setAttribute('data-theme', 'light');
  },
  ignoreElements: skipSvg ? (n) => n.tagName?.toLowerCase() === 'svg' : undefined,
});

// TEMP diagnostic: walk down the tree to the smallest element whose capture is tainted.
const describe = (n) => `${n.tagName.toLowerCase()}${n.id ? `#${n.id}` : ''}${n.className && typeof n.className === 'string' ? `.${n.className.trim().split(/\s+/).join('.')}` : ''}`;
async function findTaintSource(root) {
  let node = root;
  const path = [describe(root)];
  for (;;) {
    let next = null;
    for (const child of Array.from(node.children)) {
      const r = child.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) continue;
      // eslint-disable-next-line no-await-in-loop
      const c = await html2canvas(child, { scale: 0.5, useCORS: true, allowTaint: false, logging: false });
      if (isTainted(c)) { next = child; break; }
    }
    if (!next) break;
    path.push(describe(next));
    node = next;
  }
  // eslint-disable-next-line no-console
  console.warn('[pdf] tainted canvas source:', path.join(' > '), node);
  // eslint-disable-next-line no-console
  console.warn('[pdf] source markup:', node.outerHTML.slice(0, 2000));
}

// Paginated capture: page margins, and breaks fall between rows/blocks rather than through them.
async function addPaginated(pdf, el, { margin, keepSelector }) {
  const contentW = pdf.internal.pageSize.getWidth() - margin * 2;
  const contentH = pdf.internal.pageSize.getHeight() - margin * 2;
  const fullHeight = el.scrollHeight;
  const fullWidth = el.scrollWidth;
  const keepSel = keepSelector ? `${KEEP_TOGETHER}, ${keepSelector}` : KEEP_TOGETHER;
  const bg = lightBackgroundOf(el);

  // Pin the width so the cloned page lays the node out exactly as measured.
  const prevWidth = el.style.width;
  el.style.width = `${fullWidth}px`;
  let canvas;
  let keep;
  let keepNext;
  let blocks;
  try {
    keep = extentsOf(el, keepSel);
    blocks = blockExtentsOf(el);
    keepNext = extentsOf(el, KEEP_WITH_NEXT);
    canvas = await capture(el, fullWidth, fullHeight, false, bg);
    // Safari taints the canvas when it draws some SVG images (filters, gradients),
    // and a tainted canvas can't be exported. Retry without SVGs rather than fail.
    if (isTainted(canvas)) canvas = await capture(el, fullWidth, fullHeight, true, bg);
    if (isTainted(canvas)) await findTaintSource(el); // TEMP diagnostic
  } finally {
    el.style.width = prevWidth;
  }

  const pxPerCss = canvas.width / fullWidth;
  const mmPerCss = contentW / fullWidth;
  const pageCss = contentH / mmPerCss;

  const pages = [];
  let start = 0;
  while (start < fullHeight - 1) {
    let end = Math.min(start + pageCss, fullHeight);
    if (end < fullHeight) end = safeBreak(end, start, pageCss, keep, blocks, keepNext);

    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = Math.round((end - start) * pxPerCss);
    const ctx = slice.getContext('2d');
    ctx.fillStyle = `rgb(${bg.join(',')})`;
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, Math.round(start * pxPerCss), slice.width, slice.height, 0, 0, slice.width, slice.height);

    pages.push({ data: slice.toDataURL('image/jpeg', 0.92), height: (end - start) * mmPerCss });
    start = end;
  }

  // Fill each page with the report's background so the margins blend in.
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  pages.forEach((p, idx) => {
    if (idx > 0) pdf.addPage();
    pdf.setFillColor(...bg);
    pdf.rect(0, 0, pageW, pageH, 'F');
    pdf.addImage(p.data, 'JPEG', margin, margin, contentW, p.height);
  });
}

// Render one or more DOM nodes to a multi-page PDF and trigger a download.
// Each node starts on a new page; pages get margins and row-aware breaks (see addPaginated).
// Extracted from AllReportsPage so the client dashboard can reuse the same pattern.
export async function nodesToPdf(nodes, filename = 'report.pdf', options = {}) {
  const {
    orientation = 'landscape',
    margin = 10, // mm
    keepSelector, // extra selector for blocks a page break must not split
    beforeCapture,
    afterCapture,
  } = options;

  const list = (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean);
  if (list.length === 0) return;

  if (beforeCapture) beforeCapture();
  try {
    window.scrollTo(0, 0);
    await new Promise((r) => setTimeout(r, 400));

    const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
    for (let i = 0; i < list.length; i++) {
      if (i > 0) pdf.addPage();
      await addPaginated(pdf, list[i], { margin, keepSelector });
    }

    pdf.save(filename);
  } finally {
    if (afterCapture) afterCapture();
  }
}
